import { inject, Injectable, InjectionToken, signal } from '@angular/core';
import Keycloak, {
  KeycloakInitOptions,
  KeycloakLoginOptions,
  KeycloakServerConfig
} from 'keycloak-js';
import { Store } from '@ngrx/store';
import { UserLogout } from '../infrastructure/base-state/auth/auth.actions';

export type KeycloakFactory = (config: KeycloakServerConfig) => Keycloak;

export const KEYCLOAK_FACTORY = new InjectionToken<KeycloakFactory>(
  'KEYCLOAK_FACTORY',
  {
    providedIn: 'root',
    factory: () => (config: KeycloakServerConfig) => new Keycloak(config)
  }
);

@Injectable({
  providedIn: 'root'
})
export class KeycloakService {
  #keycloak?: Keycloak;
  #isImpersonation = false;
  readonly #store = inject(Store);
  readonly #keycloakFactory = inject(KEYCLOAK_FACTORY);
  readonly tokenSignal = signal('');

  public get token() {
    return this.#keycloak?.token;
  }

  public get sessionId() {
    return this.#keycloak?.sessionId;
  }

  async init(
    config: KeycloakServerConfig,
    initData: KeycloakInitOptions = {}
  ): Promise<boolean> {
    this.#keycloak = this.#keycloakFactory(config);

    // Refresh proactively when the access token expires, regardless of in-app
    // HTTP activity. The Apollo auth link only refreshes on outgoing GraphQL
    // requests; long-lived embeds that talk to their own API fire no app
    // requests, so without this the token silently expires and their calls
    // start failing.
    this.#keycloak.onTokenExpired = () => {
      this.updateToken().catch(err =>
        console.error('Token refresh on expiry failed', err)
      );
    };
    // Restore logout when a refresh fails — previously handled by the Apollo
    // auth link's per-request refresh.
    this.#keycloak.onAuthRefreshError = () => {
      this.#store.dispatch(new UserLogout());
    };

    this.#keycloak.onAuthRefreshSuccess = () => {
      this.tokenSignal.set(this.token ?? '');
    };

    try {
      const authenticated = await this.#keycloak.init({
        onLoad: 'check-sso',
        checkLoginIframe: false,
        silentCheckSsoFallback: false,
        silentCheckSsoRedirectUri:
          window.location.origin + '/assets/html/silent-check-sso.html',
        ...initData
      });
      if (authenticated) this.tokenSignal.set(this.token ?? '');
      return authenticated;
    } catch (error) {
      console.error('Keycloak init failed', error);
      return false;
    }
  }

  async login(options: KeycloakLoginOptions) {
    try {
      await this.#keycloak?.login(options);
    } catch (e) {
      console.error('Keycloak login failed', e);
    }
  }

  async logout(redirectUri?: string) {
    if (this.#isImpersonation) return this.logoutOfImpersonation();

    try {
      this.tokenSignal.set('');
      await this.#keycloak?.logout({
        redirectUri
      });
    } catch (e) {
      console.error('Keycloak logout failed', e);
    }
  }

  updateToken(minValidity = 5) {
    return this.#keycloak.updateToken(minValidity);
  }

  // --- Impersonation logic ---

  initImpersonation(config: KeycloakServerConfig, token: string) {
    this.#keycloak = this.#keycloakFactory(config);
    this.#keycloak.token = token;
    this.#keycloak.authenticated = true;
    this.#isImpersonation = true;
    this.tokenSignal.set(token);
    localStorage.setItem('impersonation_token', token);
  }

  logoutOfImpersonation() {
    localStorage.removeItem('impersonation_token');
    this.tokenSignal.set('');
    this.#keycloak = undefined;
    window.location.href = window.location.origin;
  }
}
